import {AxiosResponse} from "axios";

import CoreApi from "@/src/modules/core/api";
import {axios, getParams} from "@/src/modules/core/lib";
import type {GetListResponse, GetResponse, ParamsOptions} from "@/src/modules/core/types";

import {
  VEHICLE_CATALOG_PAGE_LIMIT,
  VEHICLE_CATALOG_SEARCH_MIN_CHARS,
} from "./lib/vehicle-catalog-search";
import type {
  CreateVehicleRequest,
  VehicleAccountabilityResponse,
  Vehicle,
  VehicleCreateFormData,
  UpdateVehicleRequest,
} from "./types";

class VehiclesApi extends CoreApi {
  getCreateFormData(): Promise<AxiosResponse<GetResponse<VehicleCreateFormData>>> {
    return this.get("vehicle/create");
  }

  createVehicle(
    data: CreateVehicleRequest,
  ): Promise<AxiosResponse<GetResponse<Vehicle>>> {
    return super.create("vehicle", data);
  }

  updateVehicle(
    id: number,
    data: UpdateVehicleRequest,
  ): Promise<AxiosResponse<GetResponse<Vehicle>>> {
    return super.update("vehicle", id, data);
  }

  getVehicles(
    options: ParamsOptions,
  ): Promise<AxiosResponse<GetListResponse<Vehicle>>> {
    return this.search("vehicle", options);
  }

  getVehicle(
    id: number,
    options?: ParamsOptions,
  ): Promise<AxiosResponse<GetResponse<Vehicle>>> {
    return this.getById("vehicle", id, options);
  }

  getAccountability(
    vehicleId: number,
    householdId: number,
  ): Promise<AxiosResponse<GetResponse<VehicleAccountabilityResponse>>> {
    return axios({
      url: "accountability",
      method: "get",
      params: {
        vehicle_id: vehicleId,
        household_id: householdId,
      },
    });
  }

  deleteVehicle(id: number): Promise<AxiosResponse<void>> {
    return super.delete("vehicle", id);
  }

  getUnknownVehicles(
    options?: ParamsOptions,
  ): Promise<AxiosResponse<GetListResponse<Vehicle>>> {
    const params = getParams(options);
    return axios({
      url: "vehicles/unknown",
      method: "get",
      params,
    });
  }

  /**
   * Paginated make search. Requires `filter[name]` (≥2 chars). `include_meta=1` always sent.
   */
  getVehicleMakes(
    nameContains: string,
    offset = 0,
  ): Promise<AxiosResponse<unknown>> {
    const t = nameContains.trim().slice(0, 150);
    if (t.length < VEHICLE_CATALOG_SEARCH_MIN_CHARS) {
      return Promise.reject(
        new Error(
          `getVehicleMakes: filter[name] must be at least ${VEHICLE_CATALOG_SEARCH_MIN_CHARS} characters`,
        ),
      );
    }
    const params = getParams({
      filter: {name: t},
      page: {limit: VEHICLE_CATALOG_PAGE_LIMIT, offset},
    });
    params.append("include_meta", "1");
    return axios({
      url: "vehiclemake",
      method: "get",
      params,
    });
  }

  /**
   * Paginated model search. `filter[vehicle_make_id]` and/or `filter[name]` (≥2 chars when set).
   */
  getVehicleModels(
    vehicleMakeId?: number | null,
    nameContains?: string,
    offset = 0,
  ): Promise<AxiosResponse<unknown>> {
    const t = nameContains?.trim().slice(0, 150) ?? "";
    const filter: Record<string, string | number> = {};
    const hasMake =
      vehicleMakeId != null && Number.isFinite(vehicleMakeId) && vehicleMakeId > 0;
    if (hasMake) {
      filter.vehicle_make_id = vehicleMakeId;
    }
    if (t.length >= VEHICLE_CATALOG_SEARCH_MIN_CHARS) {
      filter.name = t;
    }
    if (t.length > 0 && t.length < VEHICLE_CATALOG_SEARCH_MIN_CHARS) {
      return Promise.reject(
        new Error(
          `getVehicleModels: filter[name] must be at least ${VEHICLE_CATALOG_SEARCH_MIN_CHARS} characters`,
        ),
      );
    }
    if (Object.keys(filter).length === 0) {
      return Promise.reject(
        new Error(
          "getVehicleModels: provide vehicle_make_id and/or filter[name] with at least 2 characters",
        ),
      );
    }
    const params = getParams({
      filter,
      page: {limit: VEHICLE_CATALOG_PAGE_LIMIT, offset},
    });
    params.append("include_meta", "1");
    return axios({
      url: "vehiclemodel",
      method: "get",
      params,
    });
  }
}

export const vehiclesApi = new VehiclesApi();
